import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Modal, Select, Input } from 'antd';
import useApi from 'vl-common/src/hooks/useApi';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { Datestamp } from 'vl-common/src/components/Datestamp';
import { Task } from 'vl-common/src/types';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useIsClientAdmin } from 'vl-common/src/hooks/useIsClientAdmin';

interface ViewTaskModalProps {
  show: boolean;
  selectedTask: Task | undefined;
  onCompleted: () => void;
  onClose: () => void;
}

const ViewTaskModalContent = ({ selectedTask, onCompleted, onClose }: Omit<ViewTaskModalProps, 'show'>) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const isClientAdmin = useIsClientAdmin();
  const [selectedItem, setSelectedItem] = useState({ key: -1, value: '' });
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });

  const { data: taskAssignee, isLoading: isTaskAssigneeLoading } = useQuery({
    queryKey: ['taskAssignee', selectedTask?.task_id],
    queryFn: async () => {
      const response = await Api.task.getTaskAssignee({
        page: 1,
        rows: 999,
        sort: 'fname',
        order: 'asc'
      });
      const { assignee } = response.data;
      const opt = assignee.map((item: { assignee_guid: string; assignee: string }) => {
        return {
          key: item.assignee_guid,
          value: item.assignee_guid,
          label: item.assignee
        };
      });
      return opt || [];
    },
    gcTime: 0,
    enabled: !selectedTask?.justView
  });

  const { mutate: reassignTask, isPending: isReassignTaskLoading } = useMutation({
    mutationKey: ['reassignTask'],
    mutationFn: async () =>
      await Api.task.reassign({
        task_id: selectedTask?.task_id,
        assignee_user_guid: selectedItem.key
      }),
    onError: () => displayAlertMessage('Bad Request, Try again.'),
    onSuccess: onCompleted
  });

  useEffect(() => {
    setSelectedItem({ key: -1, value: '' });
  }, []);

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 7000);
  };

  return (
    <Modal
      title={`${selectedTask?.task_type} - ${selectedTask?.task_id} - ${selectedTask?.status}`}
      data-testid="view-task-modal"
      open
      onCancel={onClose}
      width="798px"
      centered
      footer={[
        <Button data-testid="close-task-view-modal" key="close" onClick={onClose} style={{ fontWeight: '400' }}>
          {t('Close')}
        </Button>,
        !selectedTask?.justView && isClientAdmin && (
          <Button
            data-testid="reassign-task-button"
            key="submit"
            type="primary"
            loading={isReassignTaskLoading}
            disabled={selectedItem.key === -1}
            style={{ fontWeight: '400' }}
            onClick={() => {
              if (selectedItem.key !== -1) {
                reassignTask();
              } else {
                displayAlertMessage('Please select a referrer to assign.');
              }
            }}
          >
            {t('Update')}
          </Button>
        )
      ]}
    >
      <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
      <Col
        className="vl-flex-col-center"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '12px', columnGap: '8px' }}
      >
        <div>
          <label htmlFor="patient_name" style={styles.key}>
            {t('Patient')}
          </label>
          <Input
            name="patient_name"
            id="patient_name"
            style={styles.value}
            value={selectedTask?.patient_name || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="case_selection" style={styles.key}>
            {t('Case Selection')}
          </label>
          <Input
            name="case_selection"
            id="case_selection"
            style={styles.value}
            value={selectedTask?.case_selection || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="task_type" style={styles.key}>
            {t('Task Type')}
          </label>
          <Input name="task_type" id="task_type" style={styles.value} value={selectedTask?.task_type || ''} disabled />
        </div>
        <div>
          <label htmlFor="task_subtype" style={styles.key}>
            {t('Task Sub Type')}
          </label>
          <Input
            name="task_subtype"
            id="task_subtype"
            style={styles.value}
            value={selectedTask?.task_subtype || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="creator_name" style={styles.key}>
            {t('Created By')}
          </label>
          <Input
            name="creator_name"
            id="creator_name"
            style={styles.value}
            value={selectedTask?.creator_name || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="created_date" style={styles.key}>
            {t('Date & Time Created')}
          </label>
          <div id="created_date">
            {selectedTask?.created_date && (
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderColor: '#d9d9d9',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px'
                }}
              >
                <Datestamp
                  date={new Date(selectedTask?.created_date)}
                  format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="assigned_by" style={styles.key}>
            {t('Assigned By')}
          </label>
          <Input
            name="assigned_by"
            id="assigned_by"
            style={styles.value}
            value={selectedTask?.assigned_by || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="assigned_to" style={styles.key}>
            {t('Assigned To')}
          </label>
          <Input
            name="assigned_to"
            id="assigned_to"
            style={styles.value}
            value={selectedTask?.assigned_to || ''}
            disabled
          />
        </div>
        <div>
          <label htmlFor="due_date" style={styles.key}>
            {t('Due Date')}
          </label>
          <div id="due_date">
            {selectedTask?.due_date && (
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderColor: '#d9d9d9',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px'
                }}
              >
                <Datestamp
                  date={new Date(selectedTask?.due_date)}
                  format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                />
              </div>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="closed_date" style={styles.key}>
            {t('Closed Date')}
          </label>
          <div id="closed_date">
            {selectedTask?.closed_date ? (
              <div
                style={{
                  width: '100%',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderColor: '#d9d9d9',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px'
                }}
              >
                <Datestamp
                  date={new Date(selectedTask?.closed_date)}
                  format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                />
              </div>
            ) : (
              <div>
                <Input name="closed_date" id="closed_date" style={styles.value} value="" disabled />
              </div>
            )}
          </div>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label htmlFor="task_notes" style={styles.key}>
            {t('Notes')}
          </label>
          <Input.TextArea
            name="task_notes"
            id="task_notes"
            style={styles.value}
            value={selectedTask?.task_notes || ''}
            disabled
          />
        </div>
      </Col>
      {!selectedTask?.justView && taskAssignee && isClientAdmin && (
        <Col
          style={{
            width: '90%',
            display: 'flex',
            alignItems: 'start',
            flexDirection: 'column',
            margin: '0 auto',
            gap: '8px'
          }}
        >
          <label htmlFor="assignee_select" style={{ fontSize: '14px' }}>
            {t('Assign To')}
          </label>
          <Select
            id="assignee_select"
            value={selectedItem}
            placeholder={t('Select from dropdown')}
            onChange={(value) => setSelectedItem(value)}
            data-testid="select-task-assignee"
            style={{ width: '100%' }}
            loading={isTaskAssigneeLoading}
          >
            {taskAssignee.map((data: { key: number; value: string; label: string }) => (
              <Select.Option key={data.key} value={data.value}>
                {data.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
      )}
    </Modal>
  );
};

const ViewTaskModal = ({ show, ...rest }: ViewTaskModalProps) => show && <ViewTaskModalContent {...rest} />;

export default ViewTaskModal;

const styles = {
  btnMain: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productPrimary)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productPrimary)',
    color: 'var(--productPrimaryDark)'
  },
  btnGray: {
    width: '110px',
    height: '40px',
    backgroundColor: 'var(--productGray)',
    fontSize: '14px',
    padding: '4px 10px',
    borderColor: 'var(--productGray)',
    marginRight: '10px'
  },
  select: {
    width: '400px',
    marginBottom: '30px'
  },
  key: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#000000',
    paddingBottom: '8px'
  },
  value: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#000000',
    cursor: 'text'
  },
  info: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: '30px',
    marginBottom: '30px'
  },
  btnHolders: { marginBottom: '30px' },
  body: { width: '100%' },
  title: { color: 'var(--productPink)', fontSize: '30px', fontWeight: 'bold' },
  darkTitle: { color: 'var(--productGray)', fontWeight: 'bold' },
  must: { color: 'var(--productPrimary)' }
};
